import { IRowNode } from "@ag-grid-community/core";
import { Button, Center, StyleProps } from "@chakra-ui/react";
import { dataAttr } from "@chakra-ui/utils";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableRow } from "gen/model/genericTableRow";
import React from "react";
import { ClickableElementLinkInfo } from "shared/components/clickable/ClickableElementLinkInfo";
import { DocumentDownloadLink } from "shared/components/link/DownloadDocumentLink";
import { RouteLink } from "shared/components/link/RouteLink";
import {
  DataTableColumnContext,
  DataTableContext,
} from "shared/components/table/data/DataTableContext";
import { DataTableOnButtonClickFn } from "shared/components/table/data/DataTableTypes";
import { ArrowUpRightIcon } from "shared/icons/ArrowUpRightIcon";

export interface DataTableCellLinkProps extends StyleProps {
  tableContext: DataTableContext;
  columnContext: DataTableColumnContext;
  row?: IRowNode<GenericTableRow>;
  cell?: GenericTableCell;
  linkInfo: ClickableElementLinkInfo;
  onButtonClick?: DataTableOnButtonClickFn;
  isColumnHeader?: boolean;
}

export const DataTableCellLink: React.FC<
  React.PropsWithChildren<DataTableCellLinkProps>
> = ({
  tableContext,
  columnContext,
  cell,
  linkInfo,
  onButtonClick,
  row,
  isColumnHeader,
  children,
  ...props
}) => {
  switch (linkInfo.type) {
    case "document":
      return (
        <DocumentDownloadLink key="cell" documentId={linkInfo.documentId}>
          {children}
        </DocumentDownloadLink>
      );
    case "button":
      if (!cell) {
        throw new Error("Cell is required for button links");
      }
      return (
        <Center w="100%">
          <Button
            width="100%"
            size="sm"
            colorScheme={linkInfo.colorScheme ?? "secondary"}
            isDisabled={linkInfo.isDisabled}
            isLoading={linkInfo.isLoading}
            onMouseDownCapture={(e) => e.stopPropagation()}
            onClick={
              onButtonClick
                ? (event) => {
                    onButtonClick({
                      tableContext,
                      columnContext,
                      row,
                      cell,
                      buttonType: "link",
                      event,
                    });
                  }
                : undefined
            }
            {...props}
          >
            {linkInfo.title || children}
          </Button>
        </Center>
      );
    case "generic": {
      const variant = (() => {
        if (linkInfo.variant) {
          return linkInfo.variant;
        }
        if (columnContext.column.is_header) {
          return "tableRowHeader";
        }
        if (isColumnHeader) {
          return "tableColumnHeader";
        }
        return "tableCell";
      })();
      return (
        <RouteLink
          variant={variant}
          display="inline-flex"
          alignItems="center"
          to={linkInfo.to}
          download={linkInfo.isDownload}
          data-disabled={dataAttr(linkInfo.isLoading)}
          onClick={
            cell && onButtonClick
              ? (event) => {
                  onButtonClick({
                    tableContext,
                    columnContext,
                    row,
                    cell,
                    buttonType: "link",
                    event,
                  });
                }
              : undefined
          }
          {...props}
        >
          {children}
          <ArrowUpRightIcon
            className="mb-link-icon-external"
            w="19px"
            h="19px"
          />
        </RouteLink>
      );
    }
  }
};
